// Copyright 2025-2026 - Hewlett Packard Enterprise Company
import React, { createContext, useCallback, useMemo, useState } from 'react';

import useFlags from '../hooks/useFlags';

export const SidebarContext = createContext();

export default function SidebarProvider({ children }) {
  const flags = useFlags();
  const chatBotFlag = flags.T_20241210_53281_CHATBOT_SUPPORT_BOOL();
  const ahsSupportWorkspaceFlag =
    flags.P_20260320_4980_HPE_AHS_SUPPORT_WORKSPACE();
  let showChatBot = chatBotFlag;

  if (ahsSupportWorkspaceFlag) {
    showChatBot = chatBotFlag && flags.T_20260423_4980_ENABLE_AHS_COPILOT();
  }

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [insightsTriggered, setInsightsTriggered] = useState(false);
  const [insightsMode, setInsightsMode] = useState(false);
  const [insightsSessionInitialized, setInsightsSessionInitialized] =
    useState(false);
  const [isContextualHelpOpen, setIsContextualHelpOpen] = useState(() =>
    document.body.className.split(' ').includes('helpOpen'),
  );

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    setIsChatExpanded(false);
  }, []);

  const contextualHelpOpen = useCallback(
    isOpen => {
      if (isOpen) {
        // close other sidebars
        closeChat();

        setIsContextualHelpOpen(true);
      } else {
        setIsContextualHelpOpen(false);
      }
    },
    [closeChat],
  );

  const chatOpen = useCallback(
    isOpen => {
      if (isOpen) {
        // close other sidebars
        if (isContextualHelpOpen) {
          dispatchEvent(new Event('context-help'));
        }

        setIsChatOpen(true);
      } else {
        closeChat();
        setIsChatOpen(false);
        setInsightsTriggered(false);
      }
    },
    [closeChat, isContextualHelpOpen],
  );

  const toggleIsChatExpanded = useCallback(() => {
    setIsChatExpanded(prev => !prev);
  }, []);

  const isSidebarOpen = useMemo(
    () => !!((showChatBot && isChatOpen) || isContextualHelpOpen),
    [showChatBot, isChatOpen, isContextualHelpOpen],
  );

  const toggleIsChatOpen = useCallback(() => {
    chatOpen(!isChatOpen);
  }, [chatOpen, isChatOpen]);

  const toggleIsContextualHelpOpen = useCallback(() => {
    dispatchEvent(new Event('context-help'));
  }, []);

  const value = useMemo(
    () => ({
      isChatOpen: showChatBot && isChatOpen,
      setIsChatOpen: chatOpen,
      isChatExpanded,
      toggleIsChatExpanded,
      isContextualHelpOpen,
      setIsContextualHelpOpen: contextualHelpOpen,
      isSidebarOpen,
      toggleIsChatOpen,
      toggleIsContextualHelpOpen,
      insightsTriggered,
      setInsightsTriggered,
      insightsMode,
      setInsightsMode,
      insightsSessionInitialized,
      setInsightsSessionInitialized,
    }),
    [
      showChatBot,
      chatOpen,
      contextualHelpOpen,
      isChatExpanded,
      toggleIsChatExpanded,
      isChatOpen,
      isContextualHelpOpen,
      isSidebarOpen,
      toggleIsChatOpen,
      toggleIsContextualHelpOpen,
      insightsTriggered,
      setInsightsTriggered,
      insightsMode,
      setInsightsMode,
      insightsSessionInitialized,
      setInsightsSessionInitialized,
    ],
  );

  // the current design is to only allow a single side bar panel to be open at a time
  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}
